import { createClient, FunctionRegion } from '@supabase/supabase-js';
import { config, todayISO, uid } from './utils.js';

const DEMO_STORAGE_KEY = 'suivi_routes_demo_v1';
const DEMO_SESSION_KEY = 'suivi_routes_demo_session_v1';

const nowMinus = (minutes) => new Date(Date.now() - minutes * 60000).toISOString();

function buildDemoData() {
  const date = todayISO();
  const subcontractors = [
    { id: 'sub-1', name: 'Transport Démo', active: true },
    { id: 'sub-2', name: 'Logistique Nord', active: true }
  ];

  const profiles = [
    { id: 'admin-1', full_name: 'Administrateur Démo', role: 'admin', active: true, subcontractor_id: null, email: 'admin@demo.local' },
    { id: 'driver-1', full_name: 'Jean Tremblay', role: 'driver', active: true, subcontractor_id: 'sub-1', email: 'jean@demo.local' },
    { id: 'driver-2', full_name: 'Sophie Roy', role: 'driver', active: true, subcontractor_id: 'sub-1', email: 'sophie@demo.local' },
    { id: 'driver-3', full_name: 'Marc Gagnon', role: 'driver', active: true, subcontractor_id: 'sub-2', email: 'marc@demo.local' }
  ];

  const route1 = {
    id: 'route-1', route_date: date, name: 'Route 101', driver_id: 'driver-1', status: 'planned',
    warehouse_address: config.warehouseAddress, started_at: null, completed_at: null, created_at: new Date().toISOString()
  };
  const route2 = {
    id: 'route-2', route_date: date, name: 'Route 202', driver_id: 'driver-2', status: 'in_progress',
    warehouse_address: config.warehouseAddress, started_at: nowMinus(150), completed_at: null, created_at: new Date().toISOString()
  };
  const route3 = {
    id: 'route-3', route_date: date, name: 'Route 303', driver_id: 'driver-3', status: 'completed',
    warehouse_address: config.warehouseAddress, started_at: nowMinus(300), completed_at: nowMinus(70), created_at: new Date().toISOString()
  };

  const stop = (id, route, driver, order, name, address, planned, extra = {}) => ({
    id, route_id: route, original_route_id: route, assigned_driver_id: driver, original_driver_id: driver,
    client_name: name, address, planned_order: order, planned_arrival: planned, planned_window_end: null,
    status: 'planned', is_unplanned: false, arrived_at: null, departed_at: null, outcome: null,
    driver_note: '', admin_note: '', created_at: new Date().toISOString(), ...extra
  });

  const stops = [
    stop('stop-101-1', 'route-1', 'driver-1', 1, 'Distribution Alpha', '5750 rue Ferrier, Mont-Royal', '08:30'),
    stop('stop-101-2', 'route-1', 'driver-1', 2, 'Entrepôt Bélanger', '9200 boulevard Saint-Laurent, Montréal', '09:15'),
    stop('stop-101-3', 'route-1', 'driver-1', 3, 'Marché Central', '1001 rue du Marché-Central, Montréal', '10:10'),
    stop('stop-101-4', 'route-1', 'driver-1', 4, 'Logistique Côté', '2255 rue de l’Industrie, Laval', '11:20'),
    stop('stop-101-5', 'route-1', 'driver-1', 5, 'Produits Delta', '3000 boulevard Saint-Elzéar Ouest, Laval', '12:20'),

    stop('stop-202-1', 'route-2', 'driver-2', 1, 'Client Horizon', '7400 route Transcanadienne, Saint-Laurent', '08:00', {
      status: 'completed', arrived_at: nowMinus(130), departed_at: nowMinus(112), outcome: 'delivery'
    }),
    stop('stop-202-2', 'route-2', 'driver-2', 2, 'Transport Métro', '5605 chemin Saint-François, Saint-Laurent', '09:00', {
      status: 'arrived', arrived_at: nowMinus(75)
    }),
    stop('stop-202-3', 'route-2', 'driver-2', 3, 'Industries Nova', '4300 boulevard Poirier, Saint-Laurent', '10:00'),
    stop('stop-202-4', 'route-2', 'driver-2', 4, 'Services Oméga', '2100 boulevard Hymus, Dorval', '11:00'),

    stop('stop-303-1', 'route-3', 'driver-3', 1, 'Client Rouge', '100 boulevard Bouchard, Dorval', '07:30', {
      status: 'completed', arrived_at: nowMinus(278), departed_at: nowMinus(260), outcome: 'pickup'
    }),
    stop('stop-303-2', 'route-3', 'driver-3', 2, 'Client Bleu', '230 avenue Dorval, Dorval', '08:30', {
      status: 'completed', arrived_at: nowMinus(235), departed_at: nowMinus(220), outcome: 'delivery'
    }),
    stop('stop-303-3', 'route-3', 'driver-3', 3, 'Client Vert', '1675 route Transcanadienne, Dorval', '09:30', {
      status: 'completed', arrived_at: nowMinus(185), departed_at: nowMinus(168), outcome: 'pickup_delivery'
    }),
    stop('stop-303-4', 'route-3', 'driver-3', 4, 'Client Jaune', '500 avenue Marshall, Dorval', '10:30', {
      status: 'completed', arrived_at: nowMinus(125), departed_at: nowMinus(105), outcome: 'delivery'
    })
  ];

  return { subcontractors, profiles, routes: [route1, route2, route3], stops, transfers: [], events: [] };
}

function attachRelations(data, route) {
  const driver = data.profiles.find((profile) => profile.id === route.driver_id) || null;
  const subcontractor = driver?.subcontractor_id
    ? data.subcontractors.find((item) => item.id === driver.subcontractor_id) || null
    : null;
  return {
    ...structuredClone(route),
    driver: driver ? { ...structuredClone(driver), subcontractor } : null,
    stops: data.stops
      .filter((stop) => stop.route_id === route.id)
      .sort((a, b) => a.planned_order - b.planned_order)
      .map((stop) => structuredClone(stop))
  };
}

class DemoService {
  constructor() {
    this.mode = 'demo';
    this.isDemo = true;
    this.listeners = new Set();
    if (!localStorage.getItem(DEMO_STORAGE_KEY)) {
      localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(buildDemoData()));
    }
  }

  read() {
    return JSON.parse(localStorage.getItem(DEMO_STORAGE_KEY) || JSON.stringify(buildDemoData()));
  }

  write(data) {
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(data));
  }

  currentId() {
    return localStorage.getItem(DEMO_SESSION_KEY);
  }

  notify() {
    this.listeners.forEach((listener) => listener());
  }

  async getCurrentUser() {
    const id = this.currentId();
    if (!id) return { session: null, profile: null };
    const profile = this.read().profiles.find((item) => item.id === id) || null;
    return { session: profile ? { user: { id: profile.id, email: profile.email } } : null, profile };
  }

  onAuthStateChange(callback) {
    this.listeners.add(callback);
    return { unsubscribe: () => this.listeners.delete(callback) };
  }

  async signIn(email, password) {
    const data = this.read();
    const profile = data.profiles.find((item) => item.email?.toLowerCase() === email.toLowerCase());
    if (!profile || password !== 'demo1234') throw new Error('Courriel ou mot de passe invalide.');
    if (!profile.active) throw new Error('Ce compte est désactivé.');
    localStorage.setItem(DEMO_SESSION_KEY, profile.id);
    this.notify();
    return profile;
  }

  async demoLogin(profileId) {
    const profile = this.read().profiles.find((item) => item.id === profileId);
    if (!profile) throw new Error('Compte de démonstration introuvable.');
    localStorage.setItem(DEMO_SESSION_KEY, profile.id);
    this.notify();
    return profile;
  }

  async signOut() {
    localStorage.removeItem(DEMO_SESSION_KEY);
    this.notify();
  }

  async getRoutes(date, driverId = null) {
    const data = this.read();
    return data.routes
      .filter((route) => route.route_date === date && (!driverId || route.driver_id === driverId))
      .map((route) => attachRelations(data, route))
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }

  async getRoute(routeId) {
    const data = this.read();
    const route = data.routes.find((item) => item.id === routeId);
    return route ? attachRelations(data, route) : null;
  }

  async getMyRoute(date) {
    const routes = await this.getRoutes(date, this.currentId());
    return routes[0] || null;
  }

  async listDrivers() {
    const data = this.read();
    return data.profiles
      .map((profile) => ({
        ...structuredClone(profile),
        subcontractor: data.subcontractors.find((item) => item.id === profile.subcontractor_id) || null
      }))
      .sort((a, b) => a.full_name.localeCompare(b.full_name, 'fr'));
  }

  async listSubcontractors() {
    return structuredClone(this.read().subcontractors).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }

  async saveRoute(routeInput, stopInputs) {
    const data = this.read();
    let route;
    if (routeInput.id) {
      const index = data.routes.findIndex((item) => item.id === routeInput.id);
      if (index < 0) throw new Error('Route introuvable.');
      const existingRoute = data.routes[index];
      if (existingRoute.status !== 'planned' && routeInput.driver_id !== existingRoute.driver_id) {
        throw new Error('Le chauffeur d’une route commencée ne peut plus être changé.');
      }
      route = {
        ...existingRoute,
        ...routeInput,
        driver_id: existingRoute.status === 'planned' ? routeInput.driver_id : existingRoute.driver_id,
        updated_at: new Date().toISOString()
      };
      data.routes[index] = route;
    } else {
      route = {
        id: uid(), route_date: routeInput.route_date, name: routeInput.name, driver_id: routeInput.driver_id,
        status: 'planned', warehouse_address: routeInput.warehouse_address || config.warehouseAddress,
        started_at: null, completed_at: null, created_at: new Date().toISOString()
      };
      data.routes.push(route);
    }

    const suppliedIds = new Set(stopInputs.filter((item) => item.id).map((item) => item.id));
    data.stops = data.stops.filter((stop) => {
      if (stop.route_id !== route.id) return true;
      if (suppliedIds.has(stop.id)) return true;
      return stop.status !== 'planned';
    });

    stopInputs.forEach((input, index) => {
      if (input.id) {
        const stopIndex = data.stops.findIndex((item) => item.id === input.id);
        if (stopIndex >= 0) {
          data.stops[stopIndex] = {
            ...data.stops[stopIndex],
            client_name: input.client_name,
            address: input.address,
            planned_arrival: input.planned_arrival || null,
            planned_window_end: input.planned_window_end || null,
            planned_order: index + 1,
            route_id: route.id,
            assigned_driver_id: data.stops[stopIndex].status === 'planned'
              ? route.driver_id
              : data.stops[stopIndex].assigned_driver_id,
            updated_at: new Date().toISOString()
          };
        }
      } else {
        data.stops.push({
          id: uid(), route_id: route.id, original_route_id: route.id,
          assigned_driver_id: route.driver_id, original_driver_id: route.driver_id,
          client_name: input.client_name, address: input.address,
          planned_arrival: input.planned_arrival || null, planned_window_end: input.planned_window_end || null,
          planned_order: index + 1, status: 'planned', is_unplanned: false,
          arrived_at: null, departed_at: null, outcome: null, driver_note: '', admin_note: '',
          created_at: new Date().toISOString()
        });
      }
    });

    this.write(data);
    return attachRelations(data, route);
  }

  async deleteRoute(routeId) {
    const data = this.read();
    const route = data.routes.find((item) => item.id === routeId);
    if (!route) return;
    if (route.status !== 'planned') throw new Error('Seule une route planifiée peut être supprimée.');
    data.routes = data.routes.filter((item) => item.id !== routeId);
    data.stops = data.stops.filter((item) => item.route_id !== routeId);
    this.write(data);
  }

  async duplicateRoute(routeId, targetDate, driverId) {
    const source = await this.getRoute(routeId);
    if (!source) throw new Error('Route introuvable.');
    return this.saveRoute({
      route_date: targetDate,
      name: source.name,
      driver_id: driverId || source.driver_id,
      warehouse_address: source.warehouse_address
    }, source.stops.map((stop) => ({
      client_name: stop.client_name,
      address: stop.address,
      planned_arrival: stop.planned_arrival,
      planned_window_end: stop.planned_window_end
    })));
  }

  async createDriver(input) {
    const data = this.read();
    if (data.profiles.some((profile) => profile.email?.toLowerCase() === input.email.toLowerCase())) {
      throw new Error('Un compte utilise déjà ce courriel.');
    }
    const profile = {
      id: uid(), full_name: input.full_name, email: input.email, role: input.role || 'driver',
      subcontractor_id: input.subcontractor_id || null, active: true
    };
    data.profiles.push(profile);
    this.write(data);
    return profile;
  }

  async setDriverActive(profileId, active) {
    const data = this.read();
    const profile = data.profiles.find((item) => item.id === profileId);
    if (!profile) throw new Error('Utilisateur introuvable.');
    profile.active = active;
    this.write(data);
    return profile;
  }

  async resetDriverPassword(profileId, password) {
    const profile = this.read().profiles.find((item) => item.id === profileId);
    if (!profile) throw new Error('Utilisateur introuvable.');
    if (String(password || '').length < 8) throw new Error('Le mot de passe doit contenir au moins 8 caractères.');
    return { success: true };
  }

  async createSubcontractor(name) {
    const data = this.read();
    const item = { id: uid(), name, active: true };
    data.subcontractors.push(item);
    this.write(data);
    return item;
  }

  recordEvent(data, routeId, stopId, type, location, details = {}) {
    data.events.push({
      id: uid(), route_id: routeId, stop_id: stopId || null, driver_id: this.currentId(),
      event_type: type, server_at: new Date().toISOString(), device_at: details.device_at || new Date().toISOString(),
      lat: location?.lat ?? null, lng: location?.lng ?? null, accuracy: location?.accuracy ?? null,
      details
    });
  }

  async startRoute(routeId, payload) {
    const data = this.read();
    const route = data.routes.find((item) => item.id === routeId);
    if (!route) throw new Error('Route introuvable.');
    if (!route.started_at) {
      route.started_at = new Date().toISOString();
      route.status = 'in_progress';
      route.start_lat = payload.lat;
      route.start_lng = payload.lng;
      route.start_accuracy = payload.accuracy;
      this.recordEvent(data, routeId, null, 'route_started', payload, payload);
    }
    this.write(data);
    return attachRelations(data, route);
  }

  async arriveStop(stopId, payload) {
    const data = this.read();
    const stop = data.stops.find((item) => item.id === stopId);
    if (!stop) throw new Error('Arrêt introuvable.');
    if (stop.status !== 'planned') throw new Error('Cet arrêt a déjà été commencé.');
    stop.status = 'arrived';
    stop.arrived_at = new Date().toISOString();
    stop.arrive_lat = payload.lat;
    stop.arrive_lng = payload.lng;
    stop.arrive_accuracy = payload.accuracy;
    this.recordEvent(data, stop.route_id, stop.id, 'stop_arrived', payload, payload);
    this.write(data);
    return stop;
  }

  async departStop(stopId, payload) {
    const data = this.read();
    const stop = data.stops.find((item) => item.id === stopId);
    if (!stop) throw new Error('Arrêt introuvable.');
    if (stop.status !== 'arrived') throw new Error('Il faut enregistrer l’arrivée avant le départ.');
    stop.status = 'completed';
    stop.departed_at = new Date().toISOString();
    stop.depart_lat = payload.lat;
    stop.depart_lng = payload.lng;
    stop.depart_accuracy = payload.accuracy;
    stop.outcome = payload.outcome;
    stop.driver_note = payload.driver_note || '';
    this.recordEvent(data, stop.route_id, stop.id, 'stop_departed', payload, payload);
    this.write(data);
    return stop;
  }

  async completeRoute(routeId, payload) {
    const data = this.read();
    const route = data.routes.find((item) => item.id === routeId);
    if (!route) throw new Error('Route introuvable.');
    const pending = data.stops.some((stop) => stop.route_id === routeId && !['completed', 'skipped'].includes(stop.status));
    if (pending) throw new Error('Tous les arrêts doivent être terminés avant de fermer la route.');
    route.status = 'completed';
    route.completed_at = new Date().toISOString();
    route.end_lat = payload.lat;
    route.end_lng = payload.lng;
    route.end_accuracy = payload.accuracy;
    this.recordEvent(data, routeId, null, 'route_completed', payload, payload);
    this.write(data);
    return attachRelations(data, route);
  }

  async addUnplannedStop(routeId, input) {
    const data = this.read();
    const route = data.routes.find((item) => item.id === routeId);
    if (!route) throw new Error('Route introuvable.');
    const maxOrder = Math.max(0, ...data.stops.filter((item) => item.route_id === routeId).map((item) => item.planned_order));
    const stop = {
      id: uid(), route_id: routeId, original_route_id: routeId,
      assigned_driver_id: route.driver_id, original_driver_id: route.driver_id,
      client_name: input.client_name, address: input.address,
      planned_order: maxOrder + 1, planned_arrival: null, planned_window_end: null,
      status: 'planned', is_unplanned: true, arrived_at: null, departed_at: null,
      outcome: null, driver_note: input.driver_note || '', admin_note: '', created_at: new Date().toISOString()
    };
    data.stops.push(stop);
    this.recordEvent(data, routeId, stop.id, 'stop_added', null, { reason: input.reason || '' });
    this.write(data);
    return stop;
  }

  async getTransferCandidates() {
    const data = this.read();
    const current = data.profiles.find((item) => item.id === this.currentId());
    if (!current?.subcontractor_id) return [];
    return data.profiles.filter((item) => item.role === 'driver' && item.active && item.id !== current.id && item.subcontractor_id === current.subcontractor_id);
  }

  async requestTransfer(stopId, toDriverId) {
    const data = this.read();
    const current = data.profiles.find((item) => item.id === this.currentId());
    const target = data.profiles.find((item) => item.id === toDriverId && item.role === 'driver' && item.active);
    const stop = data.stops.find((item) => item.id === stopId);
    if (!stop || stop.status !== 'planned' || stop.assigned_driver_id !== current?.id) throw new Error('Cet arrêt ne peut plus être transféré.');
    if (!current.subcontractor_id || target?.subcontractor_id !== current.subcontractor_id) throw new Error('Le transfert est limité aux chauffeurs du même sous-traitant.');
    const transfer = {
      id: uid(), stop_id: stopId, from_driver_id: stop.assigned_driver_id, to_driver_id: toDriverId,
      from_route_id: stop.route_id, to_route_id: null, requested_by: this.currentId(),
      status: 'pending', requested_at: new Date().toISOString(), responded_at: null
    };
    data.transfers.push(transfer);
    this.write(data);
    return transfer;
  }

  async getIncomingTransfers() {
    const data = this.read();
    return data.transfers
      .filter((item) => item.to_driver_id === this.currentId() && item.status === 'pending')
      .map((item) => ({
        ...structuredClone(item),
        stop: structuredClone(data.stops.find((stop) => stop.id === item.stop_id)),
        from_driver: structuredClone(data.profiles.find((profile) => profile.id === item.from_driver_id))
      }));
  }

  async respondTransfer(transferId, accept) {
    const data = this.read();
    const transfer = data.transfers.find((item) => item.id === transferId);
    if (!transfer || transfer.status !== 'pending') throw new Error('Cette demande n’est plus disponible.');
    transfer.status = accept ? 'accepted' : 'rejected';
    transfer.responded_at = new Date().toISOString();
    if (accept) {
      const stop = data.stops.find((item) => item.id === transfer.stop_id);
      const sourceRoute = data.routes.find((item) => item.id === stop.route_id);
      let targetRoute = data.routes.find((route) => route.route_date === sourceRoute.route_date && route.driver_id === transfer.to_driver_id);
      if (targetRoute?.status === 'completed' || targetRoute?.status === 'cancelled') {
        throw new Error('La route du chauffeur destinataire est déjà fermée.');
      }
      if (!targetRoute) {
        targetRoute = {
          id: uid(), route_date: sourceRoute.route_date, name: `Route ${data.profiles.find((p) => p.id === transfer.to_driver_id)?.full_name || ''}`,
          driver_id: transfer.to_driver_id, status: 'planned', warehouse_address: sourceRoute.warehouse_address,
          started_at: null, completed_at: null, created_at: new Date().toISOString()
        };
        data.routes.push(targetRoute);
      }
      const maxOrder = Math.max(0, ...data.stops.filter((item) => item.route_id === targetRoute.id).map((item) => item.planned_order));
      stop.route_id = targetRoute.id;
      stop.assigned_driver_id = transfer.to_driver_id;
      stop.planned_order = maxOrder + 1;
      transfer.to_route_id = targetRoute.id;
    }
    this.write(data);
    return transfer;
  }

  async adminReassignStop(stopId, toDriverId) {
    const data = this.read();
    const stop = data.stops.find((item) => item.id === stopId);
    if (!stop || stop.status !== 'planned') throw new Error('Seul un arrêt non commencé peut être réattribué.');
    const sourceRoute = data.routes.find((item) => item.id === stop.route_id);
    let targetRoute = data.routes.find((route) => route.route_date === sourceRoute.route_date && route.driver_id === toDriverId);
    if (targetRoute?.status === 'completed' || targetRoute?.status === 'cancelled') {
      throw new Error('La route du chauffeur destinataire est déjà fermée.');
    }
    if (!targetRoute) {
      targetRoute = {
        id: uid(), route_date: sourceRoute.route_date,
        name: `Route ${data.profiles.find((p) => p.id === toDriverId)?.full_name || ''}`,
        driver_id: toDriverId, status: 'planned', warehouse_address: sourceRoute.warehouse_address,
        started_at: null, completed_at: null, created_at: new Date().toISOString()
      };
      data.routes.push(targetRoute);
    }
    const maxOrder = Math.max(0, ...data.stops.filter((item) => item.route_id === targetRoute.id).map((item) => item.planned_order));
    stop.route_id = targetRoute.id;
    stop.assigned_driver_id = toDriverId;
    stop.planned_order = maxOrder + 1;
    this.write(data);
    return stop;
  }

  async getReport(fromDate, toDate) {
    const data = this.read();
    return data.routes
      .filter((route) => route.route_date >= fromDate && route.route_date <= toDate)
      .map((route) => attachRelations(data, route))
      .sort((a, b) => `${a.route_date}-${a.name}`.localeCompare(`${b.route_date}-${b.name}`));
  }

  async resetDemo() {
    this.write(buildDemoData());
    localStorage.removeItem(DEMO_SESSION_KEY);
    this.notify();
  }
}

class SupabaseService {
  constructor() {
    this.mode = 'supabase';
    this.isDemo = false;
    this.client = createClient(config.supabaseUrl, config.supabasePublishableKey || config.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true }
    });
  }

  async getProfile(userId) {
    const { data, error } = await this.client
      .from('profiles')
      .select('id, full_name, role, active, subcontractor_id, subcontractor:subcontractors(id, name)')
      .eq('id', userId)
      .single();
    if (error) throw error;
    if (!data.active) throw new Error('Ce compte est désactivé.');
    return data;
  }

  async getCurrentUser() {
    const { data: { session }, error } = await this.client.auth.getSession();
    if (error) throw error;
    if (!session?.user) return { session: null, profile: null };
    return { session, profile: await this.getProfile(session.user.id) };
  }

  onAuthStateChange(callback) {
    const { data } = this.client.auth.onAuthStateChange(() => callback());
    return data.subscription;
  }

  async signIn(email, password) {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return this.getProfile(data.user.id);
  }

  async signOut() {
    const { error } = await this.client.auth.signOut();
    if (error) throw error;
  }

  async getRoutes(date, driverId = null) {
    let query = this.client
      .from('routes')
      .select(`
        *,
        driver:profiles!routes_driver_id_fkey(
          id, full_name, role, active, subcontractor_id,
          subcontractor:subcontractors(id, name)
        ),
        stops:stops!stops_route_id_fkey(*)
      `)
      .eq('route_date', date)
      .order('name')
      .order('planned_order', { referencedTable: 'stops' });
    if (driverId) query = query.eq('driver_id', driverId);
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  async getRoute(routeId) {
    const { data, error } = await this.client
      .from('routes')
      .select(`
        *,
        driver:profiles!routes_driver_id_fkey(
          id, full_name, role, active, subcontractor_id,
          subcontractor:subcontractors(id, name)
        ),
        stops:stops!stops_route_id_fkey(*)
      `)
      .eq('id', routeId)
      .order('planned_order', { referencedTable: 'stops' })
      .single();
    if (error) throw error;
    return data;
  }

  async getMyRoute(date) {
    const { data: { user }, error: userError } = await this.client.auth.getUser();
    if (userError) throw userError;
    const routes = await this.getRoutes(date, user.id);
    return routes[0] || null;
  }

  async listDrivers() {
    const { data, error } = await this.client
      .from('profiles')
      .select('id, full_name, role, active, subcontractor_id, subcontractor:subcontractors(id, name)')
      .order('full_name');
    if (error) throw error;
    return data || [];
  }

  async listSubcontractors() {
    const { data, error } = await this.client.from('subcontractors').select('*').order('name');
    if (error) throw error;
    return data || [];
  }

  async saveRoute(routeInput, stopInputs) {
    let route;
    let currentRoute = null;
    if (routeInput.id) {
      const { data, error } = await this.client
        .from('routes')
        .select('id, driver_id, status')
        .eq('id', routeInput.id)
        .single();
      if (error) throw error;
      currentRoute = data;
      if (currentRoute.status !== 'planned' && routeInput.driver_id !== currentRoute.driver_id) {
        throw new Error('Le chauffeur d’une route commencée ne peut plus être changé.');
      }
    }

    const routePayload = {
      route_date: routeInput.route_date,
      name: routeInput.name,
      driver_id: currentRoute && currentRoute.status !== 'planned' ? currentRoute.driver_id : routeInput.driver_id,
      warehouse_address: routeInput.warehouse_address || config.warehouseAddress
    };

    if (routeInput.id) {
      const { data, error } = await this.client.from('routes').update(routePayload).eq('id', routeInput.id).select().single();
      if (error) throw error;
      route = data;
    } else {
      const { data, error } = await this.client.from('routes').insert(routePayload).select().single();
      if (error) throw error;
      route = data;
    }

    const { data: existing, error: existingError } = await this.client.from('stops').select('id, status, assigned_driver_id').eq('route_id', route.id);
    if (existingError) throw existingError;
    const suppliedIds = new Set(stopInputs.filter((item) => item.id).map((item) => item.id));
    const removable = (existing || []).filter((item) => !suppliedIds.has(item.id) && item.status === 'planned').map((item) => item.id);
    if (removable.length) {
      const { error } = await this.client.from('stops').delete().in('id', removable);
      if (error) throw error;
    }

    for (let index = 0; index < stopInputs.length; index += 1) {
      const input = stopInputs[index];
      const existingStop = (existing || []).find((item) => item.id === input.id);
      const payload = {
        route_id: route.id,
        assigned_driver_id: existingStop?.status && existingStop.status !== 'planned'
          ? existingStop.assigned_driver_id
          : route.driver_id,
        client_name: input.client_name,
        address: input.address,
        planned_arrival: input.planned_arrival || null,
        planned_window_end: input.planned_window_end || null,
        planned_order: index + 1
      };
      if (input.id) {
        const { error } = await this.client.from('stops').update(payload).eq('id', input.id);
        if (error) throw error;
      } else {
        const { error } = await this.client.from('stops').insert({
          ...payload,
          original_route_id: route.id,
          original_driver_id: route.driver_id
        });
        if (error) throw error;
      }
    }
    return this.getRoute(route.id);
  }

  async deleteRoute(routeId) {
    const { error } = await this.client.from('routes').delete().eq('id', routeId).eq('status', 'planned');
    if (error) throw error;
  }

  async duplicateRoute(routeId, targetDate, driverId) {
    const source = await this.getRoute(routeId);
    return this.saveRoute({
      route_date: targetDate,
      name: source.name,
      driver_id: driverId || source.driver_id,
      warehouse_address: source.warehouse_address
    }, source.stops.map((stop) => ({
      client_name: stop.client_name,
      address: stop.address,
      planned_arrival: stop.planned_arrival,
      planned_window_end: stop.planned_window_end
    })));
  }

  async createDriver(input) {
    const { data, error } = await this.client.functions.invoke(config.adminFunctionName || 'admin-users', {
      body: { action: 'createUser', ...input },
      region: FunctionRegion.CaCentral1
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data.user;
  }

  async setDriverActive(profileId, active) {
    const { data, error } = await this.client
      .from('profiles')
      .update({ active })
      .eq('id', profileId)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  async resetDriverPassword(profileId, password) {
    const { data, error } = await this.client.functions.invoke(config.adminFunctionName || 'admin-users', {
      body: { action: 'resetPassword', userId: profileId, password },
      region: FunctionRegion.CaCentral1
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  }

  async createSubcontractor(name) {
    const { data, error } = await this.client.from('subcontractors').insert({ name }).select().single();
    if (error) throw error;
    return data;
  }

  async startRoute(routeId, payload) {
    const { data, error } = await this.client.rpc('start_route', {
      p_route_id: routeId,
      p_device_at: payload.device_at,
      p_lat: payload.lat,
      p_lng: payload.lng,
      p_accuracy: payload.accuracy
    });
    if (error) throw error;
    return data;
  }

  async arriveStop(stopId, payload) {
    const { data, error } = await this.client.rpc('arrive_stop', {
      p_stop_id: stopId,
      p_device_at: payload.device_at,
      p_lat: payload.lat,
      p_lng: payload.lng,
      p_accuracy: payload.accuracy
    });
    if (error) throw error;
    return data;
  }

  async departStop(stopId, payload) {
    const { data, error } = await this.client.rpc('depart_stop', {
      p_stop_id: stopId,
      p_device_at: payload.device_at,
      p_lat: payload.lat,
      p_lng: payload.lng,
      p_accuracy: payload.accuracy,
      p_outcome: payload.outcome,
      p_driver_note: payload.driver_note || ''
    });
    if (error) throw error;
    return data;
  }

  async completeRoute(routeId, payload) {
    const { data, error } = await this.client.rpc('complete_route', {
      p_route_id: routeId,
      p_device_at: payload.device_at,
      p_lat: payload.lat,
      p_lng: payload.lng,
      p_accuracy: payload.accuracy
    });
    if (error) throw error;
    return data;
  }

  async addUnplannedStop(routeId, input) {
    const { data, error } = await this.client.rpc('add_unplanned_stop', {
      p_route_id: routeId,
      p_client_name: input.client_name,
      p_address: input.address,
      p_reason: input.reason || '',
      p_driver_note: input.driver_note || ''
    });
    if (error) throw error;
    return data;
  }

  async getTransferCandidates() {
    const { data: { user }, error: userError } = await this.client.auth.getUser();
    if (userError) throw userError;
    const { data, error } = await this.client
      .from('profiles')
      .select('id, full_name, subcontractor_id')
      .eq('role', 'driver')
      .eq('active', true)
      .neq('id', user.id)
      .order('full_name');
    if (error) throw error;
    return data || [];
  }

  async requestTransfer(stopId, toDriverId) {
    const { data, error } = await this.client.rpc('request_stop_transfer', {
      p_stop_id: stopId,
      p_to_driver_id: toDriverId
    });
    if (error) throw error;
    return data;
  }

  async getIncomingTransfers() {
    const { data: { user }, error: userError } = await this.client.auth.getUser();
    if (userError) throw userError;
    const { data, error } = await this.client
      .from('stop_transfers')
      .select(`
        *,
        stop:stops(id, client_name, address, planned_arrival, planned_order),
        from_driver:profiles!stop_transfers_from_driver_id_fkey(id, full_name)
      `)
      .eq('to_driver_id', user.id)
      .eq('status', 'pending')
      .order('requested_at');
    if (error) throw error;
    return data || [];
  }

  async respondTransfer(transferId, accept) {
    const { data, error } = await this.client.rpc('respond_stop_transfer', {
      p_transfer_id: transferId,
      p_accept: accept
    });
    if (error) throw error;
    return data;
  }

  async adminReassignStop(stopId, toDriverId) {
    const { data, error } = await this.client.rpc('admin_reassign_stop', {
      p_stop_id: stopId,
      p_to_driver_id: toDriverId
    });
    if (error) throw error;
    return data;
  }

  async getReport(fromDate, toDate) {
    const { data, error } = await this.client
      .from('routes')
      .select(`
        *,
        driver:profiles!routes_driver_id_fkey(
          id, full_name, role, active, subcontractor_id,
          subcontractor:subcontractors(id, name)
        ),
        stops:stops!stops_route_id_fkey(*)
      `)
      .gte('route_date', fromDate)
      .lte('route_date', toDate)
      .order('route_date')
      .order('name')
      .order('planned_order', { referencedTable: 'stops' });
    if (error) throw error;
    return data || [];
  }
}

export function createDataService() {
  const hasSupabase = Boolean(config.supabaseUrl && (config.supabasePublishableKey || config.supabaseAnonKey));
  return hasSupabase && config.demoMode !== true ? new SupabaseService() : new DemoService();
}
